import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Lock, ArrowRight, Loader2 } from "lucide-react";

function useCountdown(expiresAt) {
  const [ms, setMs] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setMs(Math.max(0, new Date(expiresAt).getTime() - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
  return { ms, label: `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` };
}

export default function LockedLanding() {
  const { productId } = useParams();
  const [params] = useSearchParams();
  const school = params.get("s");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.lockedLook(productId, school).then(({ data }) => setData(data)).catch(() => setErr(true)); }, [productId, school]);

  const cd = useCountdown(data?.lock?.status === "active" ? data.lock.expires_at : null);

  if (err) return <div className="h-[60vh] flex items-center justify-center"><Link to="/shop" className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm">Browse the collection</Link></div>;
  if (!data) return <div className="h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;

  const { product, school: sc, lock } = data;
  const schoolName = sc?.name || "this school";
  const isTaken = !!lock;

  return (
    <div data-testid="locked-landing-page" className="min-h-[85vh]"
         style={{ background: `radial-gradient(ellipse at top, ${sc?.primary_color || "#2a1a35"}22 0%, #FAF9F6 60%)` }}>
      <Seo
        title={`${product.name} — ${isTaken ? "Taken" : "Available"} at ${schoolName}`}
        description={`${product.name} at Heather's Boutique. ${isTaken ? `Locked at ${schoolName} — see what's still available.` : "Lock your look before someone else does."}`}
        path={`/locked/${productId}${school ? `?s=${school}` : ""}`}
        image={product.images?.[0]}
      />
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-16 grid md:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="aspect-[3/4] overflow-hidden bg-blush rounded-sm">
            <img src={product.images?.[0]} alt={product.name} className={`w-full h-full object-cover object-top ${isTaken ? "grayscale-[0.3]" : ""}`} />
          </div>
          {isTaken && (
            <div className="absolute top-5 left-5 glass-dark text-ivory px-4 py-2.5 rounded-sm flex items-center gap-2" data-testid="landing-lock-badge">
              <Lock className="w-4 h-4 text-champagne" />
              <div>
                <div className="font-label text-[9px] text-champagne">{lock.status === "purchased" ? "Secured forever" : `Locked at ${schoolName}`}</div>
                {lock.status === "active" && <div className="font-body text-sm tabular-nums">{cd.label} left</div>}
              </div>
            </div>
          )}
        </div>

        <div>
          <p className="font-label text-[10px] text-rosemetal mb-3">{schoolName} · Prom 2026</p>
          {isTaken ? (
            <>
              <h1 className="font-display text-5xl sm:text-6xl leading-[0.95]">This look is <span className="italic text-rosemetal">taken.</span></h1>
              <p className="font-body text-base text-muted-foreground mt-5 max-w-md leading-relaxed">
                The <b>{product.name}</b> is locked at {schoolName} — one dress per student, so no one shows up in the same look. But there's a whole collection waiting for you.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-5xl sm:text-6xl leading-[0.95]">The <span className="italic text-rosemetal">{product.name}</span></h1>
              <p className="font-body text-base text-muted-foreground mt-5 max-w-md leading-relaxed">{product.description}</p>
            </>
          )}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/prom-dresses" data-testid="landing-browse-btn" className="bg-ink text-ivory px-7 py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2">
              See what's still available <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/auth" data-testid="landing-join-btn" className="border border-ink px-7 py-4 font-label text-[10px] rounded-sm hover:bg-ink hover:text-ivory transition-colors text-center">
              Join {schoolName}
            </Link>
          </div>
          <p className="font-label text-[9px] text-muted-foreground mt-6">Heather's Boutique · Wisconsin Prom Network</p>
        </div>
      </div>
    </div>
  );
}
